import { useMemo, useState, useCallback } from "react"
import { useParams, Link } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import classnames from "classnames"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
  TabContent,
  TabPane,
  UncontrolledTooltip,
} from "reactstrap"

import Breadcrumbs from "components/Common/Breadcrumb"
import DataCard from "components/Common/DataCard"
import TableContainer from "components/Common/TableContainer"
import useUrlParams from "hooks/useUrlParams"
import { useAccessoriesList, ACCESSORIES_LIST_QUERY_KEY } from "hooks/useAccessoriesLists"
import { useAccessoriesUsage } from "hooks/useAccessoriesUsage"
import { formatReadableDate } from "utils/formatDate"
import { useAccessoriesStock } from "hooks/useAccessoriesStock"
import AccessoriesUsageViewModal from "pages/AccessoriesPages/AccessoriesUsage/AccessoriesUsageView"
import AccessoriesStockViewModal from "pages/AccessoriesPages/AccessoriesStock/AccessoriesStockView"
import AccessoriesAddEdit from "pages/AccessoriesPages/AccessoriesList/AccessoriesAddEdit"
import AccessoriesStockAddEdit from "pages/AccessoriesPages/AccessoriesStock/AccessoriesStockAddEdit"

const TABS = [
  { id: "vehicles", label: "Vehicle Usage", icon: "bx bx-car" },
  { id: "stock", label: "Stock Records", icon: "bx bx-purchase-tag" },
  { id: "summary", label: "Stock Summary", icon: "bx bx-box" },
]

const availabilityMeta = stock => {
  if (stock > 0) return { label: "In Stock", color: "success" }
  if (stock === 0) return { label: "Out of Stock", color: "warning" }
  return { label: "Negative Stock", color: "danger" }
}

const AccessoriesView = () => {
  const { id } = useParams()
  const { data: response, isLoading } = useAccessoriesList(id)
  const accessory = useMemo(() => response?.data ?? null, [response])

  const queryClient = useQueryClient()

  // Adding/editing stock changes this accessory's stock figures — force its query to refetch.
  const refreshAccessory = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [ACCESSORIES_LIST_QUERY_KEY] })
  }, [queryClient])

  const [params, updateParams] = useUrlParams({ tab: "vehicles" })
  const activeTab = TABS.some(t => t.id === params.tab) ? params.tab : "vehicles"
  const toggleTab = tab => {
    if (activeTab !== tab) updateParams({ tab })
  }

  document.title = `Accessory${accessory?.name ? ` | ${accessory.name}` : ""}`

  const unitName = accessory?.unit?.name || "-"
  const purchased = Number(accessory?.total_purchased ?? 0)
  const used = Number(accessory?.total_used ?? 0)
  const available = Number(accessory?.available_stock ?? 0)
  const stockMeta = availabilityMeta(available)

  const vehicleUsages = useMemo(
    () => accessory?.vehicle_usages ?? [],
    [accessory]
  )

  const distinctVehicles = useMemo(
    () => new Set(vehicleUsages.map(u => u.vehicle_id).filter(Boolean)).size,
    [vehicleUsages]
  )

  const [editModalOpen, setEditModalOpen] = useState(false)
  const [stockModalOpen, setStockModalOpen] = useState(false)

  // Fetch the full usage record on demand for the details modal.
  const [viewUsageId, setViewUsageId] = useState(null)
  const { data: usageResponse } = useAccessoriesUsage(
    viewUsageId,
    Boolean(viewUsageId)
  )

  // Fetch the full stock memo on demand for the details modal.
  const [viewStockId, setViewStockId] = useState(null)
  const { data: stockResponse } = useAccessoriesStock(
    viewStockId,
    Boolean(viewStockId)
  )

  const stockEntries = useMemo(
    () => accessory?.stock_entries ?? [],
    [accessory]
  )

  const stockColumns = useMemo(
    () => [
      {
        header: "#",
        cell: ({ row }) => (
          <span className="text-muted">{row.index + 1}</span>
        ),
      },
      {
        header: "Memo No",
        accessorKey: "memo_no",
        cell: ({ row }) => (
          <span className="fw-semibold">{row.original.memo_no || "-"}</span>
        ),
      },
      {
        header: "Purchased From",
        accessorKey: "purchased_from",
        cell: ({ row }) => row.original.purchased_from || "-",
      },
      {
        header: "Quantity",
        accessorKey: "quantity",
        cell: ({ row }) => (
          <span className="fw-semibold">{row.original.quantity}</span>
        ),
      },
      {
        header: "Unit Price",
        accessorKey: "unit_price",
        cell: ({ row }) => Number(row.original.unit_price || 0).toLocaleString(),
      },
      {
        header: "Sale Price",
        accessorKey: "sale_price",
        cell: ({ row }) => Number(row.original.sale_price || 0).toLocaleString(),
      },
      {
        header: "Purchase Date",
        accessorKey: "purchase_date",
        cell: ({ row }) =>
          row.original.purchase_date
            ? formatReadableDate(row.original.purchase_date)
            : "-",
      },
      {
        header: "Purchased By",
        accessorKey: "purchased_by",
        cell: ({ row }) => row.original.purchased_by || "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const data = row.original
          return (
            <>
              <Button
                color="primary"
                size="sm"
                onClick={() => setViewStockId(data.stock_id)}
                id={`view-stock-${data.stock_id}-${row.index}`}
              >
                <i className="mdi mdi-eye" />
              </Button>
              <UncontrolledTooltip
                target={`view-stock-${data.stock_id}-${row.index}`}
              >
                View Memo
              </UncontrolledTooltip>
            </>
          )
        },
      },
    ],
    []
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs
          title="Accessories"
          breadcrumbItem="Accessory Details"
          loading={isLoading}
        />

        {isLoading ? (
          <div className="text-center py-5">
            <Spinner />
          </div>
        ) : !accessory ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="text-center text-muted py-5">
              Accessory not found.
            </CardBody>
          </Card>
        ) : (
          <Row>
            {/* -------------------- Left column -------------------- */}
            <Col lg="4" className="mb-4">
              <Card className="border-0 shadow-sm mb-4">
                <CardBody className="p-4 text-center">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center mx-auto mb-3"
                    style={{ width: 64, height: 64 }}
                  >
                    <i className="bx bx-cog" style={{ fontSize: 28 }} />
                  </div>
                  <h4 className="mb-1 fw-semibold">{accessory.name}</h4>
                  <Badge className="badge-soft-secondary">
                    Unit: {unitName}
                  </Badge>

                  <Row className="mt-4 g-2 text-center">
                    <Col xs="4">
                      <div className="border rounded p-2">
                        <div className="text-muted small">Purchased</div>
                        <div className="fw-semibold text-info">{purchased}</div>
                      </div>
                    </Col>
                    <Col xs="4">
                      <div className="border rounded p-2">
                        <div className="text-muted small">Used</div>
                        <div className="fw-semibold text-danger">{used}</div>
                      </div>
                    </Col>
                    <Col xs="4">
                      <div className="border rounded p-2">
                        <div className="text-muted small">Available</div>
                        <div className={`fw-semibold text-${stockMeta.color}`}>
                          {available}
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Badge className={`badge-soft-${stockMeta.color} mt-3`}>
                    {stockMeta.label}
                  </Badge>

                  <div className="mt-3 d-flex justify-content-center gap-2">
                    <Button
                      color="primary"
                      outline
                      size="sm"
                      onClick={() => setEditModalOpen(true)}
                    >
                      <i className="mdi mdi-pencil-outline me-1" />
                      Edit Accessory
                    </Button>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => setStockModalOpen(true)}
                    >
                      <i className="mdi mdi-plus me-1" />
                      Add Stock
                    </Button>
                  </div>
                </CardBody>
              </Card>

              <DataCard
                title="Accessory Details"
                icon="bx bx-info-circle"
                data={[
                  { label: "Name", value: accessory.name },
                  { label: "Unit", value: unitName },
                  { label: "Slug", value: accessory.slug || "-" },
                  {
                    label: "Description",
                    value: accessory.description || "-",
                  },
                  {
                    label: "Created",
                    value: accessory.created_at
                      ? formatReadableDate(accessory.created_at)
                      : "-",
                  },
                ]}
              />
            </Col>

            {/* -------------------- Right column -------------------- */}
            <Col lg="8">
              <Nav pills className="nav-pills-custom mb-4">
                {TABS.map(tab => (
                  <NavItem key={tab.id}>
                    <NavLink
                      className={classnames({ active: activeTab === tab.id })}
                      onClick={() => toggleTab(tab.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <i className={`${tab.icon} me-2`} />
                      {tab.label}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent activeTab={activeTab}>
                {/* -------- Vehicle Usage tab -------- */}
                <TabPane tabId="vehicles">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h6 className="mb-0 fw-semibold">
                          <i className="bx bx-car me-2" />
                          Vehicles Using This Accessory
                        </h6>
                        <div className="d-flex gap-2">
                          <Badge className="badge-soft-primary">
                            {distinctVehicles} vehicle
                            {distinctVehicles === 1 ? "" : "s"}
                          </Badge>
                          <Badge className="badge-soft-secondary">
                            {vehicleUsages.length} usage record
                            {vehicleUsages.length === 1 ? "" : "s"}
                          </Badge>
                        </div>
                      </div>

                      {vehicleUsages.length === 0 ? (
                        <div className="text-center text-muted py-4">
                          This accessory has not been used on any vehicle yet.
                        </div>
                      ) : (
                        <div className="table-responsive">
                          <Table className="table-nowrap align-middle mb-0">
                            <thead className="table-light">
                              <tr>
                                <th>#</th>
                                <th>Vehicle</th>
                                <th className="text-end">Quantity</th>
                                <th>Bill Type</th>
                                <th className="text-end">Sale Price</th>
                                <th>Usage Date</th>
                                <th>Used By</th>
                                <th className="text-center">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vehicleUsages.map((row, index) => (
                                <tr key={`${row.usage_id}-${index}`}>
                                  <td className="text-muted">{index + 1}</td>
                                  <td>
                                    {row.stock_id ? (
                                      <Link
                                        to={`/stock-list/${row.stock_id}/view`}
                                        className="fw-semibold"
                                      >
                                        {row.stock_id}
                                      </Link>
                                    ) : (
                                      <span className="text-muted">N/A</span>
                                    )}
                                    {row.full_chassis ? (
                                      <div className="small text-muted">
                                        {row.full_chassis}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td className="text-end fw-semibold">
                                    {row.quantity}
                                  </td>
                                  <td>
                                    <Badge
                                      className={`badge-soft-${
                                        row.bill_type === "package"
                                          ? "info"
                                          : "secondary"
                                      }`}
                                    >
                                      {row.bill_type === "package"
                                        ? "Package"
                                        : "Individual"}
                                    </Badge>
                                  </td>
                                  <td className="text-end">
                                    {Number(
                                      row.sale_price || 0
                                    ).toLocaleString()}
                                  </td>
                                  <td className="text-muted">
                                    {row.usage_date
                                      ? formatReadableDate(row.usage_date)
                                      : "-"}
                                  </td>
                                  <td>{row.used_by || "-"}</td>
                                  <td className="text-center">
                                    <Button
                                      color="primary"
                                      size="sm"
                                      onClick={() =>
                                        setViewUsageId(row.usage_id)
                                      }
                                      id={`view-usage-${row.usage_id}-${index}`}
                                    >
                                      <i className="mdi mdi-eye" />
                                    </Button>
                                    <UncontrolledTooltip
                                      target={`view-usage-${row.usage_id}-${index}`}
                                    >
                                      View Full Details
                                    </UncontrolledTooltip>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                </TabPane>

                {/* -------- Stock Records tab -------- */}
                <TabPane tabId="stock">
                  <Card className="border-0 shadow-sm">
                    <CardBody className="p-3">
                      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                        <h6 className="mb-0 fw-semibold">
                          <i className="bx bx-purchase-tag me-2" />
                          Stock Purchase Records
                        </h6>
                        <div className="d-flex align-items-center gap-2">
                          <Badge className="badge-soft-primary">
                            {stockEntries.length} record
                            {stockEntries.length === 1 ? "" : "s"}
                          </Badge>
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => setStockModalOpen(true)}
                          >
                            <i className="mdi mdi-plus me-1" />
                            Add Stock
                          </Button>
                        </div>
                      </div>

                      <TableContainer
                        columns={stockColumns}
                        data={stockEntries}
                        theadClass="table-light"
                        tableClass="align-middle w-100 mb-0"
                      />
                    </CardBody>
                  </Card>
                </TabPane>

                {/* -------- Stock Summary tab -------- */}
                <TabPane tabId="summary">
                  <Row>
                    <Col md="12" className="mb-4">
                      <DataCard
                        title="Stock Summary"
                        icon="bx bx-box"
                        data={[
                          {
                            label: "Total Purchased",
                            value: `${purchased} ${unitName}`,
                          },
                          {
                            label: "Total Used",
                            value: `${used} ${unitName}`,
                          },
                          {
                            label: "Available Stock",
                            value: `${available} ${unitName}`,
                          },
                          { label: "Status", value: stockMeta.label },
                        ]}
                      />
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        )}

        <AccessoriesUsageViewModal
          isOpen={Boolean(viewUsageId)}
          data={usageResponse?.data ?? null}
          toggle={() => setViewUsageId(null)}
        />

        <AccessoriesStockViewModal
          isOpen={Boolean(viewStockId)}
          data={stockResponse?.data ?? null}
          toggle={() => setViewStockId(null)}
        />

        <AccessoriesAddEdit
          modal={editModalOpen}
          toggleModal={() => setEditModalOpen(false)}
          isEdit
          editedData={accessory}
        />

        <AccessoriesStockAddEdit
          modal={stockModalOpen}
          toggleModal={() => setStockModalOpen(false)}
          isEdit={false}
          preSelectedAccessory={accessory}
          onSuccess={refreshAccessory}
        />
      </Container>
    </div>
  )
}

export default AccessoriesView
